import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/DataTable";
import { motion } from "framer-motion";
import Input from "../../components/Input";
import { authService } from "../../services/auth/authService";
import {
  Society,
  SocietyResponse,
  User,
  UserResponse,
} from "../../types/api.types";
import { formatDate } from "../../utils/date";
import { userService } from "../../services/user/userService";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "../../context/SnackbarContext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { societyService } from "../../services/society/societyService";

const SocietiesPage = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState(0);

  const { showSnackbar } = useSnackbar();

  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
  const [isLoadingEnable, setIsLoadingEnable] = useState<boolean>(false);

  const [selectedSocietyToDelete, setSelectedSocietyToDelete] =
    useState<Society | null>(null);
  const [selectedSocietyToEnable, setSelectedSocietyToEnable] =
    useState<Society | null>(null);

  const theme = useTheme();

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEnableModal, setShowEnableModal] = useState<boolean>(false);

  const columns = [
    {
      header: "Nombre",
      key: "name",
    },
    {
      header: "Número de orden",
      key: "order",
    },
    {
      header: "Estado",
      key: "visible",
      render: (value: any) => {
        return (
          <Chip
            label={value?.visible ? "Activo" : "Inactivo"}
            size="small"
            sx={{
              backgroundColor: value?.visible
                ? theme.palette.primary.main + "20"
                : theme.palette.error.main + "20",
              color: value?.visible
                ? theme.palette.primary.main
                : theme.palette.error.main,
              fontWeight: "600",
              border: `1px solid ${
                value?.visible
                  ? theme.palette.primary.main + "40"
                  : theme.palette.error.main + "40"
              }`,
              borderRadius: 2,
            }}
          />
        );
      },
    },
    {
      header: "Fecha de creación",
      key: "createDate",
      render: (value: any) => (
        <Typography>{formatDate(value?.createDate)}</Typography>
      ),
    },
    {
      header: "Acciones",
      key: "actions",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <EditIcon
            sx={{ cursor: "pointer", color: "primary.main" }}
            onClick={() => navigate(`/societies/edit/${row.companyId}`)}
          />
          {!row?.visible ? (
            <VisibilityIcon
              sx={{ cursor: "pointer", color: "error.main" }}
              onClick={() => onEnable(row)}
            />
          ) : (
            <VisibilityOffIcon
              sx={{ cursor: "pointer", color: "primary.main" }}
              onClick={() => onDelete(row)}
            />
          )}
        </Box>
      ),
    },
  ];

  const [societies, setSocieties] = useState<SocietyResponse>();

  const getSocieties = async () => {
    setIsLoading(true);
    const response = await societyService.getSocieties(currentPage, 10);
    setSocieties(response);
    setIsLoading(false);
  };

  useEffect(() => {
    getSocieties();
  }, [currentPage]);

  const onDelete = async (society: Society) => {
    setShowDeleteModal(true);
    setSelectedSocietyToDelete(society);
  };

  const onEnable = async (society: Society) => {
    setShowEnableModal(true);
    setSelectedSocietyToEnable(society);
  };

  const onDeleteSociety = async () => {
    setIsLoadingDelete(true);
    try {
      const response = await societyService.deactivateSociety(
        selectedSocietyToDelete?.companyId || ""
      );
      showSnackbar(
        response?.message || "Sociedad inhabilitada correctamente",
        "success",
        3000
      );

      setShowDeleteModal(false);
      if (currentPage === 0) {
        getSocieties();
      } else {
        setCurrentPage(0);
      }
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al inhabilitar la sociedad",
        "error",
        4000
      );
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const onEnableSociety = async () => {
    setIsLoadingEnable(true);
    try {
      const response = await societyService.activateSociety(
        selectedSocietyToEnable?.companyId || ""
      );
      showSnackbar(
        response?.message || "Sociedad habilitada correctamente",
        "success",
        3000
      );
      if (currentPage === 0) {
        getSocieties();
      } else {
        setCurrentPage(0);
      }
      setShowEnableModal(false);
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al habilitar la sociedad",
        "error",
        4000
      );
    } finally {
      setIsLoadingEnable(false);
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h4">Sociedades</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/societies/create")}
              >
                Crear Sociedad
              </Button>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            style={{ marginTop: 2 }}
          >
            <DataTable
              isLoading={isLoading}
              columns={columns}
              total={societies?.total || 0}
              data={societies?.companies || []}
              page={currentPage + 1}
              onChange={(page) => {
                setCurrentPage(page - 1);
              }}
            />
          </motion.div>
        </CardContent>
      </Card>
      <Dialog
        open={showDeleteModal}
        onClose={() => {}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          ¿Estás seguro de querer inhabilitar esta sociedad?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Se inhabilitará la sociedad{" "}
            <strong style={{ color: "primary.main" }}>
              {selectedSocietyToDelete?.name}
            </strong>{" "}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isLoadingDelete}
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedSocietyToDelete(null);
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={onDeleteSociety}
            autoFocus
            disabled={isLoadingDelete}
            loading={isLoadingDelete}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showEnableModal}
        onClose={() => {}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          ¿Estás seguro de querer habilitar esta sociedad?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Se habilitará la sociedad{" "}
            <strong style={{ color: "primary.main" }}>
              {selectedSocietyToEnable?.name}
            </strong>{" "}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isLoadingEnable}
            onClick={() => {
              setShowEnableModal(false);
              setSelectedSocietyToEnable(null);
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={onEnableSociety}
            autoFocus
            disabled={isLoadingEnable}
            loading={isLoadingEnable}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SocietiesPage;
